<template>
  <div>
    <div class="flex justify-end">
      <p class="col-start-3 mr-2 grow-0 text-right">
        <span class="text-base font-medium md:text-lg">
          {{ isBlockMode ? t('reservationAmount') : t('amountToCharge') }}:
        </span>
        <span
          v-if="!showOverwriteTotalAmount"
          v-currency="amount"
          class="block text-xl font-medium md:text-2xl"
        />
        <Input
          v-else
          v-model="customTotalAmount"
          type="number"
          adornment="€"
          :max-fraction-digits="2"
        />
        <span v-if="isBlockMode" class="block text-base"
          ><a href="#" class="text-link" @click.prevent="toggleOverwriteTotalAmount">{{
            !showOverwriteTotalAmount ? t('changeAmount') : t('resetAmount')
          }}</a></span
        >
      </p>
    </div>

    <div
      class="mt-6 flex flex-col-reverse items-stretch justify-between gap-2 md:flex-row md:items-end"
    >
      <CVButton
        variant="link"
        :disabled="isSaving"
        class="max-sm:w-full"
        @click.prevent="manualReservation = !manualReservation"
      >
        {{
          !manualReservation
            ? `${isBlockMode ? t('blockManually') : t('chargeManually')} →`
            : isBlockMode
              ? t('cancelBlockManually')
              : t('cancelChargeManually')
        }}
      </CVButton>
      <div class="relative">
        <div class="grid grid-rows-1 gap-2">
          <CVButton
            v-if="isTapToPayShown"
            :is-loading="isSaving"
            variant="default"
            @click.prevent="$emit('adyenPayment', realTotalAmount, 'tapToPay')"
          >
            {{
              t(
                (isBlockMode ? 'blockViaTapToPay' : 'chargeViaTapToPay') +
                  (Capacitor.getPlatform() === 'ios'
                    ? 'AppleSpecialNeeds' // this is actually a requirement by Apple
                    : ''),
              )
            }}
            <AppleTapToPay class="h-5 w-5" />
          </CVButton>
          <CVButton
            v-if="isCardReaderShown"
            :is-loading="isSaving"
            variant="default"
            @click.prevent="$emit('adyenPayment', realTotalAmount, 'cardReader')"
          >
            {{ isBlockMode ? t('blockViaCardReader') : t('chargeViaCardReader') }} →
          </CVButton>
          <template v-if="isPaymentTerminalSelectionEnabled">
            <Select v-model="paymentTerminalId" :options="paymentTerminalOptions" />
            <CVButton
              :is-loading="isSaving"
              :disabled="!paymentTerminalId || manualReservation"
              @click.prevent="chargeCreditCardViaTerminal"
            >
              {{ isBlockMode ? t('blockViaTerminal') : t('chargeViaTerminal') }} →
            </CVButton>
          </template>
          <CVButton
            v-if="Capacitor.isPluginAvailable('Adyen')"
            outline
            @click="showTroubleshootModal = true"
          >
            {{ t('troubleshootCardReader') }}
          </CVButton>
          <CVButton
            :is-loading="isSaving"
            :disabled="manualReservation"
            outline
            @click.prevent="sendPaymentLink"
          >
            {{ t('sendPaymentLink') }} →
          </CVButton>
        </div>
        <div
          v-if="isPaymentTerminalSelectionEnabled && (isLoadingPaymentTerminals || isLoadingUser)"
          class="absolute -inset-2 flex items-center justify-center bg-black/20"
        >
          <Spinner />
        </div>
      </div>
    </div>

    <div v-if="manualReservation">
      <Divider />

      <SpecialCaseDeposit
        v-if="isBlockMode"
        :booking="props.booking"
        :amount="realTotalAmount"
        :is-saving="props.isSaving"
        @continue-without-transaction="$emit('continueWithoutTransaction', realTotalAmount)"
      />
      <SpecialCaseOpenFee
        v-else
        :is-saving="props.isSaving"
        @continue-without-transaction="$emit('continueWithoutTransaction', 0)"
      />
    </div>

    <Modal v-model="showTroubleshootModal">
      <template #title>{{ t('troubleshootCardReaderTitle') }}</template>
      <div class="space-y-4">
        <p>{{ t('troubleshootCardReaderDesc') }}</p>
        <CVButton :is-loading="isReloading" class="block" @click="reloadDevices()">
          {{ t('reloadDevices') }}
        </CVButton>
        <CVButton :is-loading="isResetting" class="block" @click="resetSession()">
          {{ t('resetSession') }}
        </CVButton>
        <CVButton class="block" @click="openPairingWindow()">
          {{ t('openPairingWindow') }}
        </CVButton>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import Spinner from '@/components/icons/Spinner.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useI18n } from 'vue-i18n';
import { usePaymentTerminalsWithQuery } from '@/queries/use-payment-terminals';
import { useMe } from '@/queries/use-users';
import { computed, ref, watch } from 'vue';
import SpecialCaseDeposit from './SpecialCaseDeposit.vue';
import SpecialCaseOpenFee from './SpecialCaseOpenFee.vue';
import Divider from '@/components/Divider.vue';
import { Alert } from '@/services/alert.service';
import { ensureAdyenPosSession, useAdyenPaymentMethods } from '@/queries/use-adyen-payments';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import AdyenPlugin from '@/capacitor-plugins/AdyenPlugin';
import { Capacitor } from '@capacitor/core';
import { useMutation } from '@tanstack/vue-query';
import AppleTapToPay from '@/components/icons/AppleTapToPay.vue';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car';
import { useFeatureFlag } from '@/hooks/use-feature-flag';

const props = defineProps<{
  booking: Booking;
  amount: number;
  isSaving?: boolean;
  mode?: 'block' | 'charge';
}>();

const emit = defineEmits<{
  (e: 'chargeCreditCardViaTerminal', paymentTerminalId: string, amount: number): void;
  (e: 'adyenPayment', amount: number, paymentMethod: 'cardReader' | 'tapToPay'): void;
  (e: 'sendPaymentLink', amount: number): void;
  (e: 'continueWithoutTransaction', amount: number): void;
}>();

const { t } = useI18n();

const isPaymentTerminalSelectionEnabled = useFeatureFlag('payment-terminal-selection');

const allocation = computed(() => props.booking.allocations[0]);
const showOverwriteTotalAmount = ref(false);
const customTotalAmount = ref<number>();
const manualReservation = ref(false);
const paymentTerminalId = ref<string>();
const showTroubleshootModal = ref(false);

const isBlockMode = computed(() => props.mode === 'block');

const { data: user, isLoading: isLoadingUser } = useMe();

const { data: paymentTerminalsData, isLoading: isLoadingPaymentTerminals } =
  usePaymentTerminalsWithQuery(
    {
      stationIds: [
        allocation.value.pickupResponsibleStation?.id ??
          getStationOfCarByDate(allocation.value.car, allocation.value.startDate).id,
      ],
      limit: 500,
    },
    computed(() => !!isPaymentTerminalSelectionEnabled.value),
  );

const adyenPaymentMethods = useAdyenPaymentMethods(() => props.booking);

// Shown without waiting for the POS session; HandoverPaymentForm checks availability on tap.
const isIphone = Capacitor.getPlatform() === 'ios' && navigator.userAgent.includes('iPhone');
const isTapToPayShown = computed(() => isIphone || !!adyenPaymentMethods.data.value?.tapToPay);
const isCardReaderShown = Capacitor.isPluginAvailable('Adyen');

const paymentTerminals = useFlattenPaginatedData(paymentTerminalsData);

watch(
  [user, paymentTerminals],
  () => {
    if (paymentTerminalId.value) return;
    if (!(user.value && paymentTerminals.value.length)) return;
    if (
      !user.value.preferredPaymentTerminal ||
      !paymentTerminals.value.some(
        (paymentTerminal) => paymentTerminal.id === user.value.preferredPaymentTerminal?.id,
      )
    ) {
      return;
    }
    paymentTerminalId.value = user.value.preferredPaymentTerminal?.id;
  },
  {
    immediate: true,
  },
);

const paymentTerminalOptions = computed(() =>
  paymentTerminals.value.map((paymentTerminal) => ({
    label: `${paymentTerminal.description} (${paymentTerminal.terminalId})`,
    value: paymentTerminal.id,
  })),
);

const toggleOverwriteTotalAmount = () => {
  showOverwriteTotalAmount.value = !showOverwriteTotalAmount.value;
  if (showOverwriteTotalAmount.value) {
    customTotalAmount.value = props.amount;
  } else {
    customTotalAmount.value = undefined;
  }
};

const realTotalAmount = computed(() => customTotalAmount.value ?? props.amount);

const chargeCreditCardViaTerminal = () => {
  if (!paymentTerminalId.value) return;
  emit('chargeCreditCardViaTerminal', paymentTerminalId.value, realTotalAmount.value);
};

const sendPaymentLink = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallySendPaymentLinkText'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('send'),
    cancelButtonText: t('cancel'),
  });
  if (!isConfirmed) return;
  emit('sendPaymentLink', realTotalAmount.value);
};

const { isPending: isReloading, mutate: reloadDevices } = useMutation({
  mutationFn: async () => {
    await adyenPaymentMethods.refetch();
  },
});

const { isPending: isResetting, mutate: resetSession } = useMutation({
  mutationFn: async () => {
    await AdyenPlugin.resetSession();
    await ensureAdyenPosSession();
    await adyenPaymentMethods.refetch();
  },
});

const { mutate: openPairingWindow } = useMutation({
  mutationFn: async () => {
    await AdyenPlugin.openDeviceSelection();
    await adyenPaymentMethods.refetch();
  },
});
</script>

<i18n lang="json">
{
  "en": {
    "reservationAmount": "Reservation Amount",
    "amountToCharge": "Amount to charge",
    "blockViaTerminal": "Block amount via terminal",
    "chargeViaTerminal": "Charge amount via terminal",
    "blockOnSavedCreditCard": "Block on saved credit card",
    "chargeFromSavedCreditCard": "Charge from saved credit card",
    "sendPaymentLink": "Send payment link",
    "blockManually": "Special case deposit",
    "cancelBlockManually": "Cancel special case deposit",
    "chargeManually": "Special case open fee",
    "cancelChargeManually": "Cancel special case open fee",
    "changeAmount": "Change Amount",
    "resetAmount": "Reset Amount",
    "reallyBlockOnSavedCreditCardText": "Do you really want to block the deposit on the credit card with the ending {maskedNumber} with which the rental amount was also paid?",
    "reallyChargeFromSavedCreditCardText": "Do you really want to charge the amount on the credit card with the ending {maskedNumber} with which the rental amount was also paid?",
    "block": "Yes, block",
    "charge": "Yes, charge",
    "reallySendPaymentLinkText": "Please only use the payment link if it did not work via the terminal or the car is delivered. The link will be automatically sent to the customer's stored email.",
    "send": "Send",
    "blockViaCardReader": "Block amount via card reader",
    "chargeViaCardReader": "Charge amount via card reader",
    "blockViaTapToPay": "Block with Tap to Pay",
    "blockViaTapToPayAppleSpecialNeeds": "Block with Tap to Pay on iPhone",
    "chargeViaTapToPay": "Charge with Tap to Pay",
    "chargeViaTapToPayAppleSpecialNeeds": "Charge with Tap to Pay on iPhone",
    "troubleshootCardReader": "Troubleshoot Card Reader",
    "troubleshootCardReaderTitle": "Troubleshoot Card Reader Connection",
    "troubleshootCardReaderDesc": "If you are having trouble connecting to the card reader, try the following actions:",
    "reloadDevices": "Reload Available Devices",
    "resetSession": "Reset Session",
    "openPairingWindow": "Reconnect Card Reader"
  },
  "de": {
    "reservationAmount": "Reservierungsbetrag",
    "amountToCharge": "Noch zu zahlender Betrag",
    "blockViaTerminal": "Betrag über Terminal blockieren",
    "chargeViaTerminal": "Betrag über Terminal einziehen",
    "blockOnSavedCreditCard": "Betrag auf hinterlegter Kreditkarte blockieren",
    "chargeFromSavedCreditCard": "Betrag von hinterlegter Kreditkarte einziehen",
    "sendPaymentLink": "Zahlungslink senden",
    "blockManually": "Sonderfall Kaution",
    "cancelBlockManually": "Sonderfall Kaution abbrechen",
    "chargeManually": "Sonderfall offener Mietbetrag",
    "cancelChargeManually": "Sonderfall offener Mietbetrag abbrechen",
    "changeAmount": "Betrag ändern",
    "resetAmount": "Betrag zurücksetzen",
    "reallyBlockOnSavedCreditCardText": "Möchtest du die Kaution wirklich auf der Kreditkarte mit der Endung {maskedNumber} blocken, mit der auch der Mietbetrag bezahlt wurde?",
    "reallyChargeFromSavedCreditCardText": "Möchtest du den Betrag wirklich auf der Kreditkarte mit der Endung {maskedNumber} einziehen, mit der auch der Mietbetrag bezahlt wurde?",
    "block": "Ja, blocken",
    "charge": "Ja, einziehen",
    "reallySendPaymentLinkText": "Bitte Zahlungslink nur verwenden, wenn es über das Terminal nicht geklappt hat, oder das Auto angeliefert wird. Der Link wird automatisch an die hinterlegte E-mail des Kunden geschickt.",
    "send": "Senden",
    "blockViaCardReader": "Betrag über Kartenleser blockieren",
    "chargeViaCardReader": "Betrag über Kartenleser einziehen",
    "blockViaTapToPay": "Mit Tap to Pay blockieren",
    "blockViaTapToPayAppleSpecialNeeds": "Mit Tap to Pay on iPhone blockieren",
    "chargeViaTapToPay": "Betrag über Tap to Pay einziehen",
    "chargeViaTapToPayAppleSpecialNeeds": "Mit Tap to Pay on iPhone einziehen",
    "troubleshootCardReader": "Kartenleser-Fehlerbehebung",
    "troubleshootCardReaderTitle": "Kartenleser-Verbindung beheben",
    "troubleshootCardReaderDesc": "Bei Problemen mit der Verbindung zum Kartenleser, versuche folgende Schritte:",
    "reloadDevices": "Verfügbare Geräte neu laden",
    "resetSession": "Session zurücksetzen",
    "openPairingWindow": "Kartenleser neu verbinden"
  },
  "es": {
    "reservationAmount": "Importe de reserva",
    "amountToCharge": "Importe a cobrar",
    "blockViaTerminal": "Bloquear importe en el terminal",
    "chargeViaTerminal": "Cobrar importe en el terminal",
    "blockOnSavedCreditCard": "Bloquear en tarjeta guardada",
    "chargeFromSavedCreditCard": "Cobrar desde tarjeta guardada",
    "sendPaymentLink": "Enviar enlace de pago",
    "blockManually": "Caso especial: fianza",
    "cancelBlockManually": "Cancelar caso especial fianza",
    "chargeManually": "Caso especial: importe pendiente",
    "cancelChargeManually": "Cancelar caso especial importe pendiente",
    "changeAmount": "Cambiar importe",
    "resetAmount": "Restablecer importe",
    "reallyBlockOnSavedCreditCardText": "¿Seguro que desea bloquear la fianza en la tarjeta que termina en {maskedNumber}, la misma con la que se pagó el alquiler?",
    "reallyChargeFromSavedCreditCardText": "¿Seguro que desea cobrar el importe en la tarjeta que termina en {maskedNumber}, la misma con la que se pagó el alquiler?",
    "block": "Sí, bloquear",
    "charge": "Sí, cobrar",
    "reallySendPaymentLinkText": "Use el enlace de pago solo si no funcionó por terminal o si el coche se entrega. El enlace se enviará automáticamente al correo del cliente registrado.",
    "send": "Enviar",
    "blockViaCardReader": "Bloquear importe con lector de tarjetas",
    "chargeViaCardReader": "Cobrar importe con lector de tarjetas",
    "blockViaTapToPay": "Bloquear con Tap to Pay",
    "blockViaTapToPayAppleSpecialNeeds": "Bloquear con Tap to Pay en iPhone",
    "chargeViaTapToPay": "Cobrar con Tap to Pay",
    "chargeViaTapToPayAppleSpecialNeeds": "Cobrar con Tap to Pay en iPhone",
    "troubleshootCardReader": "Solucionar lector de tarjetas",
    "troubleshootCardReaderTitle": "Solucionar conexión del lector",
    "troubleshootCardReaderDesc": "Si tiene problemas para conectar el lector de tarjetas, pruebe lo siguiente:",
    "reloadDevices": "Volver a cargar dispositivos disponibles",
    "resetSession": "Restablecer sesión",
    "openPairingWindow": "Volver a conectar el lector"
  }
}
</i18n>
